import React, { useEffect, useRef, useState } from 'react';
import { BsSearch, BsX } from 'react-icons/bs';
import { FaSearch } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Flex,
  Icon,
  IconButton,
  Input,
  InputGroup, InputLeftElement, Popover,
  PopoverBody, PopoverCloseButton,
  PopoverContent,
  PopoverTrigger, Text, useDisclosure
} from '@chakra-ui/react';

import { getCompanyUsers, selectCompanyUsers } from '../../redux/modules/company';
import colors from '../../styles/theme/foundation/colors';
import LoadingSpinner from '../Spinner';
import UserIcon from '../UserIcon';

const UserMultiSelectSearchDropdown = ({ userSelectOptions, onUserSelect, onUserRemove }) => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const companyUsers = useSelector(selectCompanyUsers);
  const [searchValue, setSearchValue] = useState('');
  const [filteredUsers, setFilteredUsers] = useState(null);

  useEffect(() => {
    if (!companyUsers?.length) {
      dispatch(getCompanyUsers);
    } else {
      const filteredUsersArray = companyUsers.filter(user => {
        const userFullName = `${user.userFirstName} ${user.userLastName}`.toLowerCase();

        return userFullName.includes(searchValue.toLowerCase());
      });

      setFilteredUsers(filteredUsersArray);
    }
  }, [companyUsers, dispatch, searchValue]);

  const [ownersPopIsOpen, setOwnersPopIsOpen] = useState(false);
  const open = () => setOwnersPopIsOpen(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!companyUsers?.length || !filteredUsers) {
    return (
      <Box h="200px">
        <LoadingSpinner isInline />
      </Box>
    );
  }

  const renderSearchResults = () => {
    if (filteredUsers.length === 0) {
      return (
        <Flex
          alignItems="center"
          h="100px"
          justifyContent="center"
          textAlign="center"
          w="100%"
        >
          <Text>
            {`No Results Matching ${searchValue}`}
          </Text>
        </Flex>
      );
    }

    return (
      <>
        {filteredUsers.map(user => (
          <Flex
            _hover={{
              cursor: 'pointer',
              borderRadius: 'md',
              background: colors.primaryScale['50'],
              color: colors.primaryScale['600']
            }}
            alignItems="center"
            color={colors.greyScale['600']}
            cursor="pointer"
            flexFlow="row nowrap"
            key={user.userId}
            px={2}
            py={2}
            onClick={() => {
              onUserSelect(user);
              inputRef.current.focus();
            }}
          >
            <UserIcon firstName={user.userFirstName} iconSize="30px" lastName={user.userLastName} />
            <Text noOfLines={1} pl={2} textStyle="bodyMd500">{`${user.userFirstName} ${user.userLastName}`}</Text>
          </Flex>
        ))}
      </>
    );
  };

  return (
    <Box>
      <Popover
        initialFocusRef={inputRef}
        isOpen={isOpen}
        placement="top"
        onClose={onClose}
        onOpen={onOpen}
      >
        <PopoverTrigger>
          <InputGroup onClick={open}>
            <InputLeftElement
              children={<Icon as={BsSearch} boxSize="24px" color={colors.greyScale['600']} />}
              height="40px"
              pointerEvents="none"
              zIndex={1}
            />
            <Input
              pl="40px"
              placeholder="Search Users"
              ref={inputRef}
              value={searchValue}
              variant="primary"
              onChange={e => setSearchValue(e.target.value)}
            />
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent>
          <PopoverBody>
            <Flex alignItems="center" flexFlow="row nowrap" justifyContent="space-between" mt={2}>
              <Text color="greyScale.900" mb={2} textStyle="bodyXxl500">
                PINS Users
              </Text>
            </Flex>
            <Box h="200px" overflow="auto" w="100%">
              {renderSearchResults()}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <Box>
        <Flex alignItems="center" flexFlow="row wrap" justifyContent="flex-start">
          {userSelectOptions.map(user => (
            <Flex
              alignItems="center"
              bgColor="background.main"
              borderRadius="round"
              flexFlow="row no-wrap"
              key={user.userId}
              mr={2}
              my={2}
            >
              <Box ml={1} mr={2}>
                <UserIcon
                  firstName={user.userFirstName}
                  fontSize="xxs"
                  fontWeight="medium"
                  iconSize="24px"
                  lastName={user.userLastName}
                />
              </Box>
              <Text color="greyScale.900" textStyle="bodySm">{`${user.userFirstName} ${user.userLastName}`}</Text>
              <IconButton
                icon={<Icon as={BsX} boxSize="20px" />}
                size="sm"
                variant="textGrey"
                onClick={() => onUserRemove(user)}
              />
            </Flex>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

export default UserMultiSelectSearchDropdown;
